import { Document } from '@contentful/rich-text-types';

import {
  buildAddressSwitchOptions,
  buildBroadbandSwitchOptions,
  buildHardwareDeliverySwitchOptions,
  buildHomePhoneSwitchOptions,
  buildVulnerableSwitchOptions,
  getPurchaseJourneyContent,
} from '@/content/purchase-journey-content';

const getLegacyConstants = () => getPurchaseJourneyContent().legacyConstants;

const createDynamicObject = <T extends object>(selector: () => T): T =>
  new Proxy({} as T, {
    get: (_, prop) => Reflect.get(selector(), prop),
    ownKeys: () => Reflect.ownKeys(selector()),
    getOwnPropertyDescriptor: (_, prop) => {
      const descriptor = Object.getOwnPropertyDescriptor(selector(), prop);
      return descriptor ? { ...descriptor, configurable: true } : undefined;
    },
  });

const createDynamicArray = <T>(selector: () => T[]): T[] =>
  new Proxy([] as T[], {
    get: (_, prop) => Reflect.get(selector(), prop),
    ownKeys: () => Reflect.ownKeys(selector()),
    getOwnPropertyDescriptor: (_, prop) => {
      const descriptor = Object.getOwnPropertyDescriptor(selector(), prop);
      return descriptor ? { ...descriptor, configurable: true } : undefined;
    },
  });

export const BUY_JOURNEY_CONTENT_TYPE = 'composePageNewBuyJourney';
export const BRIGHTCOVE_ACCOUNT_ID = '6122285394001';

export enum ContentType {
  HERO_OR_VIDEO_TWO_COLUMN_SECTION = 'heroVideoTwoColumnSectionCarouselsNbj',
  PRODUCT_PROMO_SECTION = 'productPromo',
  FEATURE_OR_DEVICE_SECTION = 'featuresDeviceSectionCollectionsNbj',
  TESTIMONIAL_SECTION = 'testimonialsCollections',
  CONTENT_RAIL_TABS_SECTION = 'contentTabsSectionCollectionsNbj',
  CONTENT_TAB_SECTION = 'Content Tab Section',
  CONTINUE = 'Continue',
  VIDEO_SECTION = 'Video Section',
  BUTTON = 'button',
  ACCORDION = 'accordion',
  PLANS = 'plans',
  TAB_NAVIGATION = 'tab-navigation',
  TABS = 'tabs',
  TWO_COLUMN_SECTION_CAROUSEL = 'twoColumnSectionCarouselsNbj',
  DEVICES_SECTION = 'Devices Section',
  BUNDLE_SECTION = 'Bundle Section',
  BUNDLE_CARD = 'Bundle Card',
  CONTENT_TAIL_AND_GRID_CHANNEL_COLLECTION = 'contentRailGridChannelCollectionsNbj',
  RAIL_AND_GRID_SECTION = 'Rail & Grid Section',
  CHANNEL = 'channel',
  CHANNEL_RAIL_SECTION = 'Channel Rail Section',
  FAQ_SECTION = 'faqListNbj',
  ADDRESS_CHECKER_SECTION = 'addressCheckerSectionNbj',
  PRODUCT_COLLECTION_SECTION = 'productCollectionsNbj',
  PRODUCT_COMPARE_PLANS = 'productCompareNbj',
  HERO_BANNER = 'Hero Banner',
  POPUP = 'popup',
  FEATURE_SECTION = 'Feature Section',
  ALMOST_THERE = "You're almost there!",
  ADDRESS_FORM = 'Check your address',
  TWO_COLUMN_SECTION = 'Two Cloumn',
  VIEW_PLAN_DETAILS = 'View Plan Details',
}

export enum DeviceType {
  DESKTOP = 'Desktop',
  MACBOOK = 'MacBook',
  IPAD = 'iPad',
  IPHONE = 'iPhone',
}

export enum LayoutOptions {
  HALF_WIDTH = 'Half Width',
  FULL_WIDTH = 'Full Width',
}

export const PriceFrequencyType: Record<string, string> = {
  Monthly: '/month',
};

export const HEADER = 'Header';
export const FOOTER = 'Footer';
export const LOGIN = 'Login';
export const SIGNOUT = 'Sign Out';

export const API_PREVIEW = '/api/preview';
export const PREVIEW = 'preview';
export const ENTITY_ID_SEARCH_PARAMS = 'entry_id';
export const SLUG_SEARCH_PARAMS = 'slug';

export const VIDEO_SECTION = 'Video Section';

export const ERROR_MESSAGE_NO_CONTENT_FOUND = 'No content found for the given query parameter';
export const INTERNAL_SERVER_ERROR = 'Internal Server Error';
export const CONTENTFUL_ERROR = 'ContentfulError';
export const PROJECT_NBJ = 'productNbj';
export const installationCalStartDay = 2;
export const installationCalMonths = 3;

export enum ERROR_TYPE {
  ERROR = 'error',
  FATAL = 'fatal',
  INFO = 'info',
  WARNING = 'warning',
  debug = 'debug',
}

export enum ISSUE_PRIORITY {
  HIGH = 'High', // Issues that are likely to be actionable and require immediate attention, such as ERROR and FATAL events.
  MEDIUM = 'Medium', // Issues that are likely to be actionable and require attention in the near future, including WARNING events.
  LOW = 'Low', //Issues that don't require immediate attention, including DEBUG and INFO events.
}

const defaultPhoneNumber = '09 525 5555';
const SKY_BROADBAND_CALLUS_NUMBER = defaultPhoneNumber; //default value needed as unit tests can't access envkey variables and this is used in formatPhoneNumber below
export const unformattedTelPhoneNumber = SKY_BROADBAND_CALLUS_NUMBER;
export const FIXED_ICOMS_LIMIT = 32;
export const REACT_APP_KK_CUSTOMER_ID_EXPIRE_MS = Number.parseInt(
  process.env.REACT_APP_KK_CUSTOMER_ID_EXPIRE_MS ?? '0',
  10
);
export const SKY_STARTER = 'Sky Starter';
export const SKY_BOX = 'New Sky Box';
export const PACKAGES = 'Package';
export const SKY_POD = 'Sky POD';
export const HARDWARE = 'Hardware';
export const BUNDLE = 'Bundle';
export const INCREMENT = 'INCREMENT';
export const DECREMENT = 'DECREMENT';
export const DEVICES_LIMIT_COUNT = 6;
export const MY_SKY_RECORDING = 'My Sky';
export const PRODUCT = 'PRODUCT';
export const BOX_DEVICES = 'BOX_DEVICES';
export const FOOTER_ABOUT_SECTION = 'About';
export const FOOTER_SUPPORT_SECTION = 'Support';
export const FOOTER_OTHER_SKY_SERVICES = 'Other Sky Services';
export const FOOTER_TERMS_LIST = 'Terms List';
export const FOOTER_SOCIAL_MEDIA = 'Social Media';
export const IS_MULTI_ROOM_DEVICES = false;
export const MORE_INFO = 'MORE_INFO';
export const CHANNEL_INFO = 'CHANNEL_INFO';
export const TERMS_AND_CONDITION = 'TERMS_AND_CONDITION';
export const QUICK_COMPARE = 'QUICK_COMPARE';
export const ADDON_MODAL = 'ADDON_MODAL';
export const DEVICE_SECTION = 'DEVICE_SECTION';
export const DEVICE_INFO = 'DEVICE_INFO';
export const OFFER_CARD = 'OFFER_CARD';
export const DefaultICOMSPin = '1234';
export enum DEVICE_TYPE {
  BOX = 'BOX',
  POD = 'POD',
}
export const CHECK_ADDRESS_BTN_TEXT = 'Check Address';

// Default legal copy for broadband speed disclaimer (Contentful Rich Text shape)
export const LEGAL_SPEED_DISCLAIMER = createDynamicObject(
  () => getLegacyConstants().legalSpeedDisclaimer as Document
);
export interface DateRange {
  exclude: Date[];
  latest: Date;
  earliest: Date;
}

export enum InstallationType {
  SelfInstall,
  BYOD,
}

export enum CUSTOMER_TYPE {
  REDIRECT = 'Redirect',
  BLOCK = 'block',
  ASK_LOGIN = 'AskLogin',
  ASK_OPEN_LOGIN = 'AskOpenLogin',
  REGISTER_PROFILE = 'RegisterProfile',
  CONTINUE = 'Continue',
}

export enum CATEGORY {
  TV = 'TV',
  BROADBAND = 'Broadband',
  TV_BROADBAND = 'Tv-Broadband',
  TV_BB = 'tv + broadband',
  BROADBAND_SSN = 'Broadband SSN',
}

export enum PACKAGES_LIST {
  BB_ADDONS = 'BB-Addons',
  BB_ADDONS_VOICE = 'BB-Addons-Voice',
  BB_UPGRADE = 'BB-Upgrade',
  PACKAGE_BB_ADDONS_VOICE = 'Package-BB-Addons-voice',
  TV_POD = 'Tv-Pod',
  TV_BOX = 'Tv-Box',
}

export enum SKYHOMEPLAN {
  TOP10 = '201TP',
  TOP20 = '202TP',
}

export enum CATEGORY_TYPE {
  HARDWARE = 'Hardware',
  PACKAGE = 'Package',
}

export const validCategories = new Set([
  CATEGORY.TV.toLocaleLowerCase(),
  CATEGORY.BROADBAND.toLocaleLowerCase(),
]);

export enum MODAL_TYPE {
  MORE_INFO = 'MORE_INFO',
  CHANNEL_INFO = 'CHANNEL_INFO',
  TERMS_AND_CONDITION = 'TERMS_AND_CONDITION',
  QUICK_COMPARE = 'QUICK_COMPARE',
  ADDON_MODAL = 'ADDON_MODAL',
  DEVICE_SECTION = 'DEVICE_SECTION',
  DEVICE_INFO = 'DEVICE_INFO',
  BANK_SEARCH = 'BANK_SEARCH',
  SPEED_INFO = 'SPEED_INFO',
  MODAL_HOME_PHONE_PLANS = 'MODAL_HOME_PHONE_PLANS',
  CART_INFO = 'CART_INFO',
  CONTENT_INFO = 'CONTENT_INFO',
  SKY_HOME_PHONE_NUMBER = 'SKY_HOME_PHONE_NUMBER',
  SKY_HOME_PHONE_PROVIDER = 'SKY_HOME_PHONE_PROVIDER',
}

export enum MODAL_DEVICE_INFO_TYPE {
  SKY_BOX = '624',
  SKY_POD = '616',
  SKY_ROUTER = '603BB',
  STATIC_IP = '199BB',
  SKY_BOOSTER = '604BB',
  DEFAULT = 'default',
}

export const STEP_POINTER = createDynamicArray(() => getLegacyConstants().stepPointer);

export const ESTIMATE_BILL_LABELS = createDynamicObject(() => ({
  ONE_MONTH_IN_ADVANCE: getLegacyConstants().estimateBillLabels.oneMonthInAdvance,
  FIRST_BILL_ESTIMATE: getLegacyConstants().estimateBillLabels.firstBillEstimate,
  FIRST_MONTH: getLegacyConstants().estimateBillLabels.firstMonth,
  TITLE: getLegacyConstants().estimateBillLabels.title,
  ESTIMATE_BILL_BTN_TEXT: getLegacyConstants().estimateBillLabels.estimateBillBtnText,
}));

export const SWITCH_BTN_FOR_BROADBAND = createDynamicArray(buildBroadbandSwitchOptions);

export const SWITCH_BTN_FOR_HOMEPHONE = createDynamicArray(buildHomePhoneSwitchOptions);

export const SWITCH_BTN_FOR_INSTALLATION = createDynamicArray(() => [
  {
    label: getLegacyConstants().switchButtons.installation[0]?.label ?? 'Yes',
    value: 'Yes',
  },
  {
    label: getLegacyConstants().switchButtons.installation[1]?.label ?? 'No',
    value: 'No',
  },
]);

export const DOG_OPTIONS = createDynamicArray(() => [
  {
    title: getLegacyConstants().switchButtons.dogOptions[0]?.title ?? 'Yes',
    value: 'Yes',
  },
  {
    title: getLegacyConstants().switchButtons.dogOptions[1]?.title ?? 'No',
    value: 'No',
  },
]);

export const SWITCH_BTN_FOR_ADDRESS = createDynamicArray(buildAddressSwitchOptions);

export const SWITCH_BTN_FOR_HARDWARE_DELIVERY = createDynamicArray(
  buildHardwareDeliverySwitchOptions
);

export const SWITCH_BTN_FOR_VULNERABLE = createDynamicArray(buildVulnerableSwitchOptions);

export const SELECTED_DIALOG_INFO = createDynamicObject(
  () => getLegacyConstants().selectedDialogInfo
);

export enum BroadbandJourney {
  FibreSwap,
  IntactONT,
  NewFibre,
  Unservicable,
}

export enum ProductType {
  Cable = 'cable',
  Data = 'data',
  Voice = 'voice',
}
export enum BroadbandReady {
  Unknown,
  FibreOK,
  FibreFuture,
  FibreNever,
}

export enum StorageNames {
  ACQUISITION_FORM_STORAGE = 'acquisition-form-storage',
  ORDER_STORAGE = 'order-store',
  PRODUCT_STORAGE = 'product-store',
  CUSTOMER_STORAGE = 'customer-store',
  DEVICE_STORAGE = 'device-store',
  COUPON_STORAGE = 'coupons-store',
  NEW_ORDER_STORE = 'new-order-store',
  HOMEPHONE_STORAGE = 'home-phone-store',
}

export const CHECKOUT_TABS = {
  plan: 'plan',
  aboutYou: 'about-you',
  checkout: 'checkout',
  confirmation: 'confirmation',
};

export const AREA_CODES = [
  { value: '', label: '' },
  { value: '03', label: '03' },
  { value: '04', label: '04' },
  { value: '06', label: '06' },
  { value: '07', label: '07' },
  { value: '09', label: '09' },
  { value: '020', label: '020' },
  { value: '021', label: '021' },
  { value: '022', label: '022' },
  { value: '023', label: '023' },
  { value: '024', label: '024' },
  { value: '025', label: '025' },
  { value: '027', label: '027' },
  { value: '028', label: '028' },
  { value: '029', label: '029' },
];

export const BROADBAND_AREA_CODES = [
  { value: '', label: '' },
  { value: '020', label: '020' },
  { value: '021', label: '021' },
  { value: '022', label: '022' },
  { value: '023', label: '023' },
  { value: '024', label: '024' },
  { value: '025', label: '025' },
  { value: '027', label: '027' },
  { value: '028', label: '028' },
  { value: '029', label: '029' },
];

export const HOME_PHONE_AREA_CODES = [
  { value: '', label: '' },
  { value: '03', label: '03' },
  { value: '04', label: '04' },
  { value: '06', label: '06' },
  { value: '07', label: '07' },
  { value: '09', label: '09' },
];

export const Banks = [
  { value: '0', label: 'ANZ' },
  { value: '1', label: 'ASB' },
  { value: '2', label: 'BNZ' },
  { value: '3', label: 'CITIBANK' },
  { value: '4', label: 'HSBC' },
  { value: '5', label: 'ICBC' },
  { value: '6', label: 'KIWIBANK' },
  { value: '7', label: 'TSB' },
  { value: '8', label: 'WESTPAC' },
  { value: '9', label: 'Other' },
];
export const BanksWithRadio = [
  { id: 1, name: 'ANZ', value: 'ANZ', checked: false },
  { id: 2, name: 'ASB', value: 'ASB', checked: false },
  { id: 3, name: 'BNZ', value: 'BNZ', checked: false },
  { id: 4, name: 'CITIBANK', value: 'CITIBANK', checked: false },
  { id: 5, name: 'HSBC', value: 'HSBC', checked: false },
  { id: 6, name: 'ICBC', value: 'ICBC', checked: false },
  { id: 7, name: 'KIWIBANK', value: 'KIWIBANK', checked: false },
  { id: 8, name: 'TSB', value: 'TSB', checked: false },
  { id: 9, name: 'WESTPAC', value: 'WESTPAC', checked: false },
  { id: 10, name: 'Other', value: 'Other', checked: false },
];

export const DeviceNotSelectedItem = createDynamicObject(
  () => getLegacyConstants().fallbackItems.deviceNotSelected
);

export const AddonsNotSelectedItem = createDynamicObject(
  () => getLegacyConstants().fallbackItems.addonsNotSelected
);

export const ProductLabelItem = createDynamicArray(
  () => getLegacyConstants().fallbackItems.productLabels
);

export const COUPON_MAPPING_FOR_OFFER_TEXT = [
  {
    skuIds: '624,616',
    couponCode: '04998',
  },
  {
    skuIds: '202,238,242,233',
    couponCode: ['01321', '01323'],
  },
  {
    skuIds: '233',
    couponCode: '01344',
  },
];

export const PRODUCT_SKU_PACKAGE_NAME = {
  SKY_BUNDLES: 'skyBundles',
  SKY_BOX: 'skyBox',
  SKY_POD: 'skyPod',
};

export const BUNDLE_MOVIE_COUPON = '01344';
export const VOUCHER_PRODUCT_SKUS_LIST = ['702C'];
export const DISCOUNT_SKUS_LIST = ['150BB', '151BB', '152BB'];
export const ADDITIONAL_SKUS_LIST = ['801BB', '800BB', '602BB']; // 602BB router - with Own equipment
export const productSkuIds = {
  skyBundles: {
    primary: '238,242',
    customAttribute: null,
    label: 'Ent,Sport',
  },
  skyBox: {
    primary: '624,830,615',
    customAttribute: null,
    label: '',
  },
  skyPod: {
    primary: '616,887', //841 removed(new sky box self install)
    customAttribute: null,
    label: '',
  },
  movies: {
    primary: '233',
    customAttribute: null,
    label: '',
  },

  //the below one still not used
  skyEntertainment: {
    primary: '238',
    customAttribute: null,
    label: 'Ent,Sport',
  },
  skySport: {
    primary: '242',
    customAttribute: null,
    label: 'Ent,Sport',
  },
  starter: {
    primary: '202',
    customAttribute: null,
    label: '',
  },
  access: {
    primary: '122',
    customAttribute: null,
    label: '',
  },

  /* The old box in the new world. */
  skyBoxCharge: {
    primary: '620',
    customAttribute: null,
    label: '',
  },

  paceBox500GB: {
    primary: '600',
    customAttribute: null,
    label: '',
  },

  paceBox1TB: {
    primary: '610',
    customAttribute: null,
    label: '',
  },

  kaonBox500GBUpfront: {
    primary: '619',
    customAttribute: null,
    label: '',
  },

  paceBox500GBUpfront: {
    primary: '605',
    customAttribute: null,
    label: '',
  },

  kaonBox1TBUpfront: {
    primary: '617',
    customAttribute: null,
    label: '',
  },

  paceBox1TBUpfront: {
    primary: '611',
    customAttribute: null,
    label: '',
  },

  kaonBox1TB: {
    primary: '618',
    customAttribute: null,
    label: '',
  },

  /* The old boxes that we don't sell anymore. */
  legacyBoxCharge: {
    primary: '600,619,605,618,610,617,611',
    customAttribute: null,
    label: '',
  },

  // skyBox: {
  //   primary: '620,615',
  //   customAttribute: '001,207,602,603',
  //   label: '',
  // },

  mySky: {
    primary: '620,612',
    customAttribute: '001,207,602,603',
    label: '',
  },

  mySkyPlus: {
    primary: '618,612,614',
    customAttribute: '001,207,602,603',
    label: '',
  },

  arrowBox: {
    primary: '624',
  },

  arrowBoxBlack: {
    primary: '625',
  },

  arrowBoxOneOff: {
    primary: '643',
  },

  /* Only comes with 643. */
  arrowUpfrontBoxFee: {
    //primary: isAcquisition ? '830C' : '830',
    primary: '830',
    customAttribute: null,
  },

  arrowMonthly: {
    primary: '642',
  },

  arrowBoxRecording: {
    primary: '612',
  },
  upfrontRecording: {
    primary: '613',
  },

  /* 1TB Storage. */
  recording1TB: {
    primary: '614',
  },

  /* VOD Only. */
  noRecording: {
    primary: '615',
  },

  koanSkyBoxPlusRecording: {
    primary: '617',
  },

  koanSkyBoxRecording: {
    primary: '619',
  },

  // skyPod: {
  //   primary: '616',
  // },

  skyPodOneOffFee: {
    primary: '887',
    customAttribute: null,
  },

  // note: this only a temporary sky stream sku
  skyStream: {
    primary: '900',
  },

  paceSkyBox: {
    primary: '600,615',
    customAttribute: '001,207,602,603',
    label: '',
  },
  paceMySky: {
    primary: '600,612',
    customAttribute: '001,207,602,603',
    label: '',
  },
  paceMySkyPlus: {
    primary: '610,612,614',
    customAttribute: '001,207,602,603',
    label: '',
  },
  upFrontMySky: {
    primary: '619,613',
    customAttribute: '001,207,602,603',
    label: '',
  },
  upFrontMySkyPlus: {
    primary: '617,613',
    customAttribute: '001,207,602,603,612,614',
    label: '',
  },
  upFrontPaceMySky: {
    primary: '605,613',
    customAttribute: '001,207,602,603',
    label: '',
  },
  upFrontPaceMySkyPlus: {
    primary: '611,613',
    customAttribute: '001,207,602,603,612,614',
    label: '',
  },
  broadcastTier: {
    primary: '205',
    customAttribute: null,
    label: '',
  },
  broadbandForcedOccurrence: {
    primary: '001BB',
    customAttribute: null,
    label: '',
  },
  broadbandLightningFastWiFi: {
    primary: '101BB',
    customAttribute: null,
    label: 'Lightning Fast WiFi',
  },
  broadbandLightningFastWiFiBoost: {
    primary: '102BB',
    customAttribute: null,
    label: 'Lightning Fast WiFi Boost',
  },
  broadbandWifi100: {
    primary: '110BB',
    customAttribute: null,
    label: 'Speedy WiFi',
  },
  broadbandWifi100Boost: {
    primary: '111BB',
    customAttribute: null,
    label: 'Speedy WiFi Boost',
  },
  broadbandEssnFibre: {
    //created new Starter Product i.e 122BB
    // primary: '120BB',
    primary: '122BB',
    customAttribute: null,
    label: 'Fibre Starter',
  },
  broadbandEssnFibreOld: {
    //created new Starter Product i.e 122BB
    primary: '120BB',
    customAttribute: null,
    label: 'Fibre Starter',
  },
  broadbandEssnFibBoostOld: {
    primary: '121BB',
    customAttribute: null,
    label: 'Fibre Starter Boost',
  },
  broadbandEssnFibBoost: {
    //created new Starter boost Product
    primary: '123BB',
    customAttribute: null,
    label: 'Fibre Starter Boost',
  },
  broadbandPerfectInstall: {
    primary: '804BB',
    customAttribute: null,
    label: 'PerfectInstall',
  },
  skyRouter: {
    primary: '603BB',
    customAttribute: null,
    label: '',
  },
  meshOccurrence: {
    primary: '601BB',
    customAttribute: null,
  },
  meshDevice: {
    primary: '604BB',
    customAttribute: null,
    label: '',
  },
  broadbandSelfInstall: {
    primary: '801BB',
    customAttribute: null,
    label: 'SelfInstall',
  },
  broadbandBringYourOwnDevice: {
    primary: '602BB',
    customAttribute: null,
    label: 'BYOD',
  },
  broadbandStaticIP: {
    primary: '199BB',
    customAttribute: null,
    label: '',
  },
  summerSetIdentifier: {
    primary: '901BB',
    customAttribute: null,
    label: '',
  },
  summerSet: {
    primary: '119BB',
    customAttribute: null,
    label: '',
  },
  sport: {
    primary: '242',
    customAttribute: null,
    label: '',
  },
  entertainment: {
    primary: '238',
    customAttribute: null,
    label: '',
  },
  entertainmentLight: {
    primary: '112',
    customAttribute: null,
    label: '',
  },
  tfcPremimumChannel: {
    primary: '265',
    customAttribute: null,
    label: '',
  },
  entertainmentPremium: {
    primary: '113',
    customAttribute: null,
    label: '',
  },
  entertainmentPlatinum: {
    primary: '114',
    customAttribute: null,
    label: '',
  },
  soho: {
    primary: '260',
    customAttribute: null,
    label: '',
  },
  arts: {
    primary: '256',
    customAttribute: null,
    label: '',
  },
  countryTv: {
    primary: '258',
    customAttribute: null,
    label: '',
  },
  rialto: {
    primary: '232',
    customAttribute: null,
    label: '',
  },
  skyBoxOTC: {
    primary: '725',
    customAttribute: null,
    label: '',
  },
  mySkyOTC: {
    primary: '736',
    customAttribute: null,
    label: '',
  },
  mySkyPlusOTC: {
    primary: '736',
    customAttribute: null,
    label: '',
  },
  paperBilling: {
    primary: 'PAPERLESS',
    customAttribute: null,
    label: '',
  },
  broadbandSelfInstallOTC: {
    primary: '800BB',
    customAttribute: null,
    label: '',
  },
  multiroom: {
    primary: '207',
    customAttribute: null,
    label: '',
  },
  broadcastService: {
    primary: '927',
    customAttribute: null,
    label: '',
  },
  voice: {
    primary: '100TP',
    customAttribute: '001TP',
    label: '',
  },
  voiceSummerSet: {
    primary: '110TP',
    customAttribute: null,
    label: '',
  },
  voiceSummerSetOpt: {
    primary: '112TP',
    customAttribute: null,
    label: '',
  },
  voiceAustraliaCalls: {
    primary: '200TP',
    customAttribute: null,
    label: '',
  },
  voiceNationalCalls: {
    primary: '102TP',
    customAttribute: null,
    label: '',
  },
  voiceTop10Calls: {
    primary: '201TP',
    customAttribute: null,
    label: '',
  },
  voiceTop20Calls: {
    primary: '202TP',
    customAttribute: null,
    label: '',
  },
  broadbandStarterDiscountOneGig: {
    primary: '150BB',
  },
  broadbandStarterDiscountHundredMeg: {
    primary: '151BB',
  },
  //created new Product for 50/10 (5$ credit)
  broadbandStarterCreditDiscount: {
    primary: '152BB',
  },
  voucherForcedOccurrence: {
    primary: '001SS',
  },
  voucherEligible: {
    primary: '101SS',
  },
  voucherActive: {
    primary: '102SS',
  },

  /* This is actually tech install no charge. */
  ArrowTechInstall: {
    primary: '837',
  },

  ArrowTechVisit: {
    primary: '838',
  },

  /* Pass New Sky Box self install if user doesn't want tech visit. */
  ArrowSelfInstall: {
    primary: '841',
  },

  otcBoxSwap: {
    primary: '728',
    customAttribute: null,
  },
  otcBoxSwapUpFront: {
    primary: '724',
    customAttribute: null,
  },

  premiumEntertainment: {
    primary: '128',
  },
  lightEntertainment: {
    primary: '127',
  },
  ultimateEntertainment: {
    primary: '129',
  },

  frontBookMySky: {
    primary: '166',
  },

  skyDigitalMusic: {
    primary: '250',
  },
};

export const paymentCheckBoxLabels = createDynamicObject(
  () => getLegacyConstants().paymentCheckBoxLabels
);

export const contextGAMessageForBlockCustomer = () =>
  getLegacyConstants().contextGAMessageForBlockCustomer;

export enum RailType {
  Digital_Rail = 'Digital_Rail',
  Content_Catalog = 'Content_Catalog',
  Manual = 'Manual', // Optional: for fallback or default case
}
export enum LayoutType {
  Landscape = 'Landscape',
  Portrait = 'Portrait',
}

export const PhoneNumberType = 'primary';
export const accountType = 'Cheque';
export const countryCode = '+64';
export const dateFormat = 'yyyy-MM-dd';
export const defaultTime = 'PM';
export const notShown = 'Not Shown';
export const isNotShownProvider = (provider?: string) =>
  /^not\s+shown(?:\s+in\s+(?:the\s+)?list)?$/i.test(provider?.trim() ?? '');
export const getProviderDisplayName = (provider?: string) =>
  isNotShownProvider(provider) ? 'Other' : provider;

export enum FormName {
  aboutYou = 'About You',
  abandoncart = 'save cart',
  addressForm = 'Check your address',
  hardwareAddress = 'Hardware Adress Details',
  broadbandForm = 'Broadband Form',
  broadbandFormDetails = 'Broadband Form Details',
  preferredInstallationForm = 'Preferred Installation Form',
  paymentForm = 'Payment Form',
  addressFormStep = 'fibre available',
  fibreAvailable = 'Fibre Available',
  fibreUnavailable = 'Fibre Unavailable',
  enterAddress = 'Enter address',
  confirmationForm = 'Confirmation',
  preferredInstallationFormDetails = 'Preferred Installation Form Details',
  addressFormDetails = 'Address Details',
  paymentDetails = 'Payment Details',
  aboutYouDetails = 'About You Details',
  installationForm = 'Installation Form',
  installationFormDetails = 'Installation Details',
  checkout = 'Checkout',
  plan = 'plan',
  fiberAddressCheck = `Lets's check your address for fiber`,
  bankDetails = 'Bank Details',
  paymentMethod = 'Payment Method',
  homePhone = 'Home Phone Details',
  voucherForm = 'Cart Voucher Form',
  leadNurture = 'Lead Nurture',
  enterYourEmailAdress = 'Enter your email address',
}
export enum FieldType {
  Text = 'Text',
  Select = 'Select',
  Date = 'Date',
  Radio = 'Radio',
  Time = 'Time',
  Checkbox = 'Checkbox',
}

export enum FieldName {
  Address = 'Your Address',
  Homephone = 'Home Phone',
  hardwareAddress = 'Hardware Adress Details',
}

export enum FormSection {
  Address = 'Your Address',
}

export enum FormStep {
  Address = 'Enter address',
  UseDifferentDeliveryAddress = 'Use different address',
}

export enum PackageType {
  SPORT = 'Sport',
  SKY_ENT = 'sky entertainment',
  SKY_MOVIES = 'sky movies',
  SKY_STARTER = 'sky starter',
}

export const COUPON_REPLACEMENTS = [
  {
    OLD_CODES: ['01321', '01319', '04999'],
    NEW_CODE: '01323',
  },
];

export const REMOVE_COUPONS = [
  '01277',
  '01281',
  '01282',
  '01287',
  '01288',
  '01289',
  '01292',
  '01296',
  '09044BB',
  '09046BB',
  '09056BB',
  '09057BB',
  '01298',
  '01299',
  '04952',
  '01303',
  '01302',
  '01293',
  '09060BB',
  '09061BB',
  '09059BB',
  '09079BB',
  '09080BB',
  '09082BB',
  '09084BB',
  '09085BB',
  '09083BB',
];

export const CREDIT_OFFER = 'credit_offer';

export enum VoucherProductTypeEnums {
  DTH = 'DTH',
  BROADBAND = 'Broadband',
  DTH_AND_BROADBAND = 'DTH & Broadband',
  DTH_OR_BROADBAND = 'DTH or BROADBAND',
}

//default coupons when there is no coupons to be sent in order request payload
export const defaultCableCoupons = '01197'; //for TV
export const defaultDataNoContractCoupons = '09101BB'; //for BB/voice no contract
export const defaultData12MonthContract = '09102BB'; //for BB/voice 12 month contract

//field order in about you page to trigger scroll to first erro field
export const fieldOrder = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  dateOfBirth: '',
  email: '',
  serviceAddress: '',
  prefferedInstallationDate: '',
  installationDate: '',
  installationTime: '',
  alternateName: '',
  alternatePhoneAreaCode: '',
  alternatePhoneLineNumber: '',
  alternatePhoneNumber: '',
  dogsAtHome: '',
  broadbandAreaCode: '',
  broadbandPhoneNumber: '',
  changeProviderCheck: '',
  currentServiceProvider: '',
  providerAccountNumber: '',
  deliveryInstructions: '',
  homePhoneNumber: '',
  currentProvider: '',
  accountNumberForHomePhone: '',
  phoneNumberFromList: '',
};

export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
